import { useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import {
    BarChartIcon,
    DevicesFotaAddIcon,
    ExpandMoreIcon,
    FileDownloadIcon,
    FileUploadIcon,
    MoveFilesIcon,
    QrCodeIcon,
    SwapHorizIcon,
    SyncIcon
} from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import ActionToolbarButtonWrapper from "components/DesignComponents/Button/ActionToolbarButtonWrapper";
import ListChoice from "components/DesignComponents/ListChoice";
import Menu, {
    centeredAnchorOrigin,
    centeredTransformOrigin
} from "components/DesignComponents/Menu";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import AuthContext from "context/auth/authContext";
import RoutesContext from "context/routes/routesContext";
import TableContext from "context/table/tableContext";
import { getEnvironment } from "shared/functions";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { usePermissions } from "shared/hooks/usePermissions";
import { USER_ACCESS_LEVELS } from "shared/types/enums";

import AddDevice from "../dialogs/Devices/AddDevice";
import AssignCanPackage from "../dialogs/Devices/AssignCanPackage";
import AutoSync from "../dialogs/Devices/AutoSync";
import CreateTask from "../dialogs/Devices/CreateTask";
import ExportDeviceData from "../dialogs/Devices/ExportDeviceData";
import ImportData from "../dialogs/Devices/ImportData";
import MoveDevices from "../dialogs/Devices/MoveDevices";
import TransferDevice from "../dialogs/Devices/TransferDevice";
import { DEVICE_ACTIONS } from "../types";
import ContentFrameWithButton from "./components/ContentFrameWithButton";
import { useDialog, useMenu } from "./hooks";

const DevicesBar = () => {
    const { isAdminUser, isSupportUser, user } = useContext(AuthContext);
    const { toggleStatistics } = useContext(RoutesContext);
    const { totalRows, isInitialLoading } = useContext(TableContext);

    const { t } = useTranslation();
    const { toSm, toMd, fromLg, toLg } = useMediaQueries();
    const { anchorEl, handleOpenMenu, handleCloseMenu } = useMenu();

    const {
        isCreateTaskNotAllowed,
        isUpdateDeviceNotAllowed,
        isExportDeviceNotAllowed,
        isTransferDeviceNotAllowed,
        PERMISSIONS,
        getNoPermissionTooltipTitle
    } = usePermissions();

    const { isOpenDialog, openDialog, closeDialog } =
        useDialog(handleCloseMenu);

    const { isProductionEnv } = getEnvironment();

    const renderTableStatisticsButton = () =>
        toMd ? (
            <TooltipGeneral
                disableInteractive
                disableTouchListener
                placement={TOOLTIP_PLACEMENT.Top}
                title={t("Button##table statistics")}
            >
                <span>
                    <Button
                        variant={BUTTON_VARIANTS.IconOnly}
                        size={BUTTON_SIZES.Small}
                        color={BUTTON_COLORS.White}
                        icon={<BarChartIcon />}
                        onClick={toggleStatistics}
                        disabled={totalRows === 0}
                    />
                </span>
            </TooltipGeneral>
        ) : (
            <Button
                variant={BUTTON_VARIANTS.IconLeft}
                size={BUTTON_SIZES.Small}
                color={BUTTON_COLORS.White}
                icon={<BarChartIcon />}
                onClick={toggleStatistics}
                disabled={totalRows === 0}
            >
                {t("Button##table statistics")}
            </Button>
        );

    const isUserOrSuperUser = () => {
        const accessLevel = user?.access_level;

        return (
            accessLevel === USER_ACCESS_LEVELS.User ||
            accessLevel === USER_ACCESS_LEVELS.SuperUser
        );
    };

    return (
        <>
            <ContentFrameWithButton
                button={!isSupportUser && renderTableStatisticsButton()}
            >
                <ActionToolbarButtonWrapper
                    permissionName={PERMISSIONS.TaskCreate}
                >
                    <Button
                        fullWidth={toSm}
                        hasRectangleLoader={toSm && isInitialLoading}
                        variant={BUTTON_VARIANTS.TextOnly}
                        size={BUTTON_SIZES.Small}
                        color={BUTTON_COLORS.Primary}
                        disabled={isCreateTaskNotAllowed}
                        onClick={() => openDialog(DEVICE_ACTIONS.CreateTask)}
                    >
                        {t("Button##create task")}
                    </Button>
                </ActionToolbarButtonWrapper>

                {fromLg && (
                    <ActionToolbarButtonWrapper
                        permissionName={PERMISSIONS.DeviceUpdate}
                    >
                        <Button
                            variant={BUTTON_VARIANTS.TextOnly}
                            size={BUTTON_SIZES.Small}
                            color={BUTTON_COLORS.White}
                            disabled={isUpdateDeviceNotAllowed}
                            onClick={() =>
                                openDialog(DEVICE_ACTIONS.MoveDevices)
                            }
                        >
                            {t("Button##move")}
                        </Button>
                    </ActionToolbarButtonWrapper>
                )}

                <ActionToolbarButtonWrapper>
                    <Button
                        fullWidth={toSm}
                        hasRectangleLoader={toSm && isInitialLoading}
                        variant={BUTTON_VARIANTS.IconRight}
                        size={BUTTON_SIZES.Small}
                        color={BUTTON_COLORS.White}
                        icon={<ExpandMoreIcon />}
                        onClick={handleOpenMenu}
                    >
                        {t("Button##more actions")}
                    </Button>
                </ActionToolbarButtonWrapper>
            </ContentFrameWithButton>

            <Menu
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorEl={anchorEl}
                anchorOrigin={centeredAnchorOrigin}
                transformOrigin={centeredTransformOrigin}
                css={css({
                    "& .MuiMenu-paper": {
                        minWidth: "222px"
                    }
                })}
            >
                {isAdminUser && (
                    <ListChoice
                        title={t("Button##add device")}
                        iconLeft={<DevicesFotaAddIcon />}
                        onClick={() => openDialog(DEVICE_ACTIONS.AddDevice)}
                    />
                )}

                {toLg && (
                    <ListChoice
                        title={t("Button##move")}
                        iconLeft={<MoveFilesIcon />}
                        disabled={isUpdateDeviceNotAllowed}
                        tooltipText={getNoPermissionTooltipTitle(
                            PERMISSIONS.DeviceUpdate
                        )}
                        onClick={() => openDialog(DEVICE_ACTIONS.MoveDevices)}
                    />
                )}

                {!isProductionEnv && isUserOrSuperUser() && (
                    <ListChoice
                        title={t("Button##auto sync")}
                        iconLeft={<SyncIcon />}
                        disabled={isUpdateDeviceNotAllowed}
                        tooltipText={getNoPermissionTooltipTitle(
                            PERMISSIONS.DeviceUpdate
                        )}
                        onClick={() => openDialog(DEVICE_ACTIONS.AutoSync)}
                    />
                )}

                <ListChoice
                    title={t("Button##import")}
                    iconLeft={<FileDownloadIcon />}
                    disabled={isUpdateDeviceNotAllowed}
                    tooltipText={getNoPermissionTooltipTitle(
                        PERMISSIONS.DeviceUpdate
                    )}
                    onClick={() => openDialog(DEVICE_ACTIONS.ImportDevices)}
                />

                <ListChoice
                    title={t("Button##export")}
                    iconLeft={<FileUploadIcon />}
                    disabled={isExportDeviceNotAllowed}
                    tooltipText={getNoPermissionTooltipTitle(
                        PERMISSIONS.DeviceExport
                    )}
                    onClick={() => openDialog(DEVICE_ACTIONS.ExportDeviceData)}
                />

                {isAdminUser && (
                    <ListChoice
                        title={t("General##assign can package")}
                        iconLeft={<QrCodeIcon />}
                        onClick={() =>
                            openDialog(DEVICE_ACTIONS.AssignCanPackage)
                        }
                    />
                )}

                {!isSupportUser && (
                    <ListChoice
                        title={t("Button##transfer")}
                        iconLeft={<SwapHorizIcon />}
                        disabled={isTransferDeviceNotAllowed}
                        tooltipText={getNoPermissionTooltipTitle(
                            PERMISSIONS.DeviceTransfer
                        )}
                        onClick={() =>
                            openDialog(DEVICE_ACTIONS.TransferDevice)
                        }
                    />
                )}
            </Menu>

            <CreateTask
                isOpen={isOpenDialog[DEVICE_ACTIONS.CreateTask]}
                close={closeDialog}
            />

            <AddDevice
                isOpen={isOpenDialog[DEVICE_ACTIONS.AddDevice]}
                close={closeDialog}
            />

            <MoveDevices
                isOpen={isOpenDialog[DEVICE_ACTIONS.MoveDevices]}
                close={closeDialog}
            />

            <AutoSync
                isOpen={isOpenDialog[DEVICE_ACTIONS.AutoSync]}
                close={closeDialog}
            />

            <ImportData
                isOpen={isOpenDialog[DEVICE_ACTIONS.ImportDevices]}
                close={closeDialog}
            />

            <ExportDeviceData
                isOpen={isOpenDialog[DEVICE_ACTIONS.ExportDeviceData]}
                close={closeDialog}
            />

            <AssignCanPackage
                isOpen={isOpenDialog[DEVICE_ACTIONS.AssignCanPackage]}
                close={closeDialog}
            />

            <TransferDevice
                isOpen={isOpenDialog[DEVICE_ACTIONS.TransferDevice]}
                close={closeDialog}
            />
        </>
    );
};

export default DevicesBar;
